import { observable } from 'mobx'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Dimensions, Platform, ScaledSize } from 'react-native'
import { ThemeContext } from 'styled-components/native'

const useBreakPoint = (isEnabled: boolean) => {
  const [dimensions, setDimensions] = useState(Dimensions.get('window'))

  useEffect(() => {
    let onDimensionsChange: any
    if (isEnabled) {
      onDimensionsChange = ({ window }: { window: ScaledSize }) => {
        setDimensions(window)
      }

      Dimensions.addEventListener('change', onDimensionsChange)
    }

    return () => {
      if (isEnabled) {
        Dimensions.removeEventListener('change', onDimensionsChange)
      }
    }
  }, [isEnabled])
  const theme = useContext(ThemeContext)
  if (isEnabled) {
    const breakpoints: Array<number> = theme?.breakpoints ?? []
    let selectValue = 0
    for (let i = 0; i < breakpoints.length; i++) {
      if (dimensions.width < breakpoints[i]) {
        break
      }
      selectValue++
    }
    return selectValue
  }
  return 0
}

export function isSafari() {
  return (
    navigator?.vendor &&
    navigator?.vendor.indexOf('Apple') > -1 &&
    navigator?.userAgent &&
    navigator?.userAgent.indexOf('CriOS') === -1 &&
    navigator?.userAgent.indexOf('FxiOS') === -1
  )
}

export const isIE = () => {
  if (Platform.OS === 'android' || Platform.OS === 'ios') {
    return false
  }
  return /*@cc_on!@*/ false || !!document?.documentMode
}

export const supportMobxProxy = function () {
  try {
    observable({ supportMobxProxy: 'test' })
    return true
  } catch (err) {
    return false
  }
}

export function useBreakPointProps<T>(props: any): T {
  const isEnabled = useMemo(() => {
    return Object.keys(props)
      .filter((item) => {
        item !== 'children'
      })
      .some((item) => Array.isArray(item))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const breakpoint = useBreakPoint(isEnabled)
  const modifiedProps = !isEnabled
    ? props
    : Object.keys(props)
        .filter((item) => item !== 'children')
        .reduce((acc, item) => {
          let originalValue = props[item as keyof typeof props]
          if (Array.isArray(originalValue)) {
            originalValue =
              breakpoint < originalValue.length ? originalValue[breakpoint] : originalValue[originalValue.length - 1]
          }
          return { ...acc, [item]: originalValue }
        }, {})
  return modifiedProps as T
}

export function getOS() {
  const userAgent = window.navigator.userAgent,
    platform = window.navigator.platform,
    macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'],
    windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'],
    iosPlatforms = ['iPhone', 'iPad', 'iPod']
  let os

  if (macosPlatforms.indexOf(platform) !== -1) {
    os = 'Mac OS'
  } else if (iosPlatforms.indexOf(platform) !== -1) {
    os = 'iOS'
  } else if (windowsPlatforms.indexOf(platform) !== -1) {
    os = 'Windows'
  } else if (/Android/.test(userAgent)) {
    os = 'Android'
  } else if (!os && /Linux/.test(platform)) {
    os = 'Linux'
  }
  return os
}

export const rupayCardMatrix = [
  { start: '5085000000', end: '5089999999' }, // (508[5-9][0-9]{12})
  { start: '6069850000', end: '6079849999' }, // (6069[8-9][0-9]{11})|(607[0-8][0-9]{12})|(6079[0-8][0-9]{11})
  { start: '6080010000', end: '6082009999' }, // (608[0-5][0-9]{12})
  { start: '6082010000', end: '6083009999' }, // covered in prev
  { start: '6083010000', end: '6083509999' }, // covered in prev
  { start: '6083510000', end: '6085009999' }, // covered in prev
  { start: '6521500000', end: '6528499999' },
  // (6521[5-9][0-9]{11})|(652[2-9][0-9]{12})|(6530[0-9]{12})|(6531[0-4][0-9]{11})
  { start: '6528500000', end: '6530499999' }, // covered in prev
  { start: '6530500000', end: '6531499999' }, // covered in prev
  { start: '3528000000', end: '3589999999' }, // covered in prev
  { start: '3000000000', end: '3059999999' },
  // (30[0-5][0-9]{13}) | (3095[0-9]{12}) | (36[0-9]{14}) | (3[8-9][0-9]{14})
  { start: '3095000000', end: '3095999999' }, // covered in prev
  { start: '3600000000', end: '3699999999' }, // covered in prev
  { start: '3800000000', end: '3999999999' }, // covered in prev
  { start: '6011000000', end: '6011039999' },
  // (60110[0-3][0-9]{10}) | (60110[5-9][0-9]{10}) |(6011[2-4][0-9]{11})
  // |(601174[0-9]{10}) | (60117[7-9][0-9]{10}) | (6011[8-9][6-9][0-9]{10})
  { start: '6011050000', end: '6011099999' }, // covered in prev
  { start: '6011200000', end: '6011499999' }, // covered in prev
  { start: '6011740000', end: '6011749999' }, // covered in prev
  { start: '6011770000', end: '6011799999' }, // covered in prev
  { start: '6011860000', end: '6011999999' }, // covered in prev
  { start: '6440000000', end: '6521499999' },
  // ((64[4-8][0-9]{2}|649[0-8][0-9]|6499[0-9]|65[01][0-9]{2}|6520[0-9]|6521[0-4]))([0-9]{11})
  { start: '6531500000', end: '6599999999' }, // ((6531[5-9]|653[2-9][0-9]|65[4-9][0-9]{2}))([0-9]{11})
  { start: '8172000000', end: '8198999999' }, // ((817[2-9]|818[0-9]|819[0-8]))([0-9]{12})
  { start: '8199000000', end: '8201999999' },
]

export const cardVendorPrefixes = {
  maestro: [
    '502260',
    '504433',
    '504434',
    '504435',
    '504437',
    '504645',
    '504681',
    '504753',
    '504775',
    '504809',
    '504817',
    '504834',
    '504848',
    '504884',
    '504973',
    '504993',
    '508125',
    '508126',
    '508159',
    '508192',
    '508227',
    '56',
    '57',
    '58',
    '600206',
    '603123',
    '603741',
    '603845',
    '622018',
    '67',
    '502045',
    '503898',
  ],
  discover: ['8', '6011', '65', '644', '645', '646', '647', '648', '649'],
  diners: ['300', '301', '302', '304', '305', '309', '36', '38', '39'],
  masterCard: ['5', '22', '23', '24', '25', '26', '27'],
  amex: ['34', '37'],
  visa: ['4'],
  none: ['0'],
}

export enum CardVendors {
  NONE = 'none',
  RUPAY = 'rupay',
  MAESTRO = 'maestro',
  DISCOVER = 'discover',
  DINERS = 'diners',
  MASTER_CARD = 'mastercard',
  AMEX = 'amex',
  VISA = 'visa',
}

const getPrefixValuesRupay = () => {
  const prefixList: Array<string> = []
  for (const item of rupayCardMatrix) {
    const panLowLn = Number(item.start.substring(0, 6))
    const panHighLn = Number(item.end.substring(0, 6))
    const diff = panHighLn - panLowLn
    for (let index = 0; index <= diff; index += 1) {
      prefixList.push((panLowLn + index).toString())
    }
  }
  return prefixList
}

export const convertHexToRGBA = (hexCode: any, opacity: any) => {
  let hex = hexCode.replace('#', '')

  if (hex.length === 3) {
    hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`
  }

  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  return `rgba(${r},${g},${b},${opacity / 100})`
}

const RuPayPrefixList: Array<string> = getPrefixValuesRupay()

export const CardVendorData = [
  {
    vendor: CardVendors.NONE,
    prefix: cardVendorPrefixes.none,
  },
  {
    vendor: CardVendors.DINERS,
    prefix: cardVendorPrefixes.diners,
    imagePath: require('../assets/images/ic_cards_diner.png'),
  },
  {
    vendor: CardVendors.RUPAY,
    prefix: RuPayPrefixList,
  },
  {
    vendor: CardVendors.MAESTRO,
    prefix: cardVendorPrefixes.maestro,
  },
  {
    vendor: CardVendors.DISCOVER,
    prefix: cardVendorPrefixes.discover,
    imagePath: require('../assets/images/ic_cards_discover.png'),
  },
  {
    vendor: CardVendors.MASTER_CARD,
    prefix: cardVendorPrefixes.masterCard,
  },
  {
    vendor: CardVendors.AMEX,
    prefix: cardVendorPrefixes.amex,
  },
  {
    vendor: CardVendors.VISA,
    prefix: cardVendorPrefixes.visa,
  },
]

const splitingStrategy: any = {
  13: [4, 10, 13], // 4-6-3
  14: [4, 10, 14], // 4-6-4
  15: [4, 10, 15], // 4-6-5
  19: [4, 8, 12, 16, 19], // 4-4-4-4-3
}

export const formatCardNumber = (cardNumberWithoutSpace: string, cardLength: number = 16) => {
  let formattedNumber = ''
  let index = 0

  let isSplittingStrategyDefined = false
  let formatArray = []

  if (splitingStrategy[cardLength]) {
    isSplittingStrategyDefined = true
    formatArray = splitingStrategy[cardLength].slice()
  }

  while (index < cardNumberWithoutSpace.length) {
    // If any other specific pattern is required
    if (isSplittingStrategyDefined) {
      if (index !== 0 && index % formatArray[0] === 0) {
        formatArray.shift()
        formattedNumber += ' '
      }
    }
    // For usual 16 digit long card number
    else {
      if (index !== 0 && index % 4 === 0) {
        formattedNumber += ' '
      }
    }
    formattedNumber += cardNumberWithoutSpace.charAt(index)
    index += 1
  }
  return formattedNumber
}

let prevExpLength = 0
export const formatExpiryDate = (expiryWithoutSpace: string) => {
  let formattedExpiry = ''
  let index = 0
  if (expiryWithoutSpace.length === 1 && parseInt(expiryWithoutSpace, 10) > 1) {
    expiryWithoutSpace = '0' + expiryWithoutSpace
  }
  if (expiryWithoutSpace.length === 2 && parseInt(expiryWithoutSpace, 10) > 12) {
    expiryWithoutSpace = '0' + expiryWithoutSpace
  }
  while (index < expiryWithoutSpace.length) {
    formattedExpiry += expiryWithoutSpace.charAt(index)
    if (index !== 0 && prevExpLength !== expiryWithoutSpace.length && index === 1) {
      formattedExpiry += ' / '
    }
    index += 1
  }
  prevExpLength = expiryWithoutSpace.length
  return formattedExpiry
}

const hasAnyPrefixes = (cardnumber: string, prefixes: Array<string>) => {
  for (const prefix of prefixes) {
    if (cardnumber.startsWith(prefix)) {
      return true
    }
  }
  return false
}

export const getCardVendorAndImage = (cardNumber: string) => {
  for (const data of CardVendorData) {
    if (hasAnyPrefixes(cardNumber, data.prefix)) {
      return [data.vendor, data.imagePath]
    }
  }
  return [CardVendors.NONE]
}

export const maxCardLength = {
  none: 16,
  rupay: 16,
  maestro: 19,
  discover: 16,
  mastercard: 16,
  diners: 16, // diners us and canada has 16 digits which is not supported now
  amex: 15,
  visa: 16,
}

export const cardFormatSpacersLength = {
  none: 3,
  rupay: 3,
  maestro: 4,
  discover: 3,
  mastercard: 3,
  diners: 3,
  amex: 2,
  visa: 3,
}

export const maxPossibleCardLength = {
  none: [16],
  rupay: [16],
  maestro: [16, 19],
  discover: [16],
  mastercard: [16],
  diners: [14, 16],
  amex: [15],
  visa: [13, 16],
}

export type maxCardLengthOptions = keyof typeof maxPossibleCardLength

export const maxCVVLength = {
  none: 3,
  rupay: 3,
  maestro: 3,
  discover: 3,
  mastercard: 3,
  diners: 3,
  amex: 4,
  visa: 3,
}

export const ListDimens = {
  // Width and Height
  LIST_DEFAULT_CONTAINER_WIDTH: '100%',
  LIST_DEFAULT_SEPARATOR_HEIGHT: 1,
  LIST_CAROUSEL_DOTS_SIZE: 8,
  LIST_HEADER_CONTAINER_HEIGHT: 36,
  LIST_CELL_HEIGHT: 40,

  // Border Radius and Width
  LIST_FOOTER_BORDER_WIDTH: 1,
  LIST_CAROUSEL_DOTS_BORDER_RADIUS: 4,

  // Padding and Margins
  LIST_BOTTOM_CONTAINER_MARGIN: 16,
  LIST_CAROUSELIMAGE_MARGIN: 30,
  LIST_CAROUSELIMAGE_WIDTH: 30,
  LIST_CAROUSELIMAGE_HEIGHT: 15,
  LIST_BAR_MARGIN: 40,
  LIST_DETAIL_CONTAINER_PADDING: 20,
  LIST_DETAIL_CONTAINER_MARGIN: 20,
  LIST_DETAIL_TEXT_MARGIN: 20,
  LIST_CAROUSEL_DOTS_MARGIN: 8,
  LIST_HEADER_CONTAINER_PADDING: 2,
  LIST_DETAIL_EXTRA_MAX: 20,
  LIST_THREE_LINE_CONTAINER_MARGIN: 16,
  DEFAULT_IMAGE_HEIGHT: 150,
}

export const SpaceDimens = {
  // Width and Height
  SPACE_DEFAULT_WIDTH: 1,
  SPACE_DEFAULT_HEIGHT: 1,
  SEPARATOR_DEFAULT_WIDTH: '100%',
  SEPARATOR_DEFAULT_HEIGHT: '100%',
  // Margin and Padding
  SPACE_DEFAULT: 1,
  SPACE_1x: 1,
  SPACE_2x: 2,
  SPACE_SMALL: 16,
  SPACE_24: 24,
  SPACE_MEDIUM: 32,
  SPACE_LARGE: 64,
}

export const CarousalDimens = {
  CAROUSAL_BORDER_RADIUS: 4,
  CAROUSAL_FULL_HEIGHT: 184,
  CAROUSAL_FULL_MARGIN: 4,
  CAROUSAL_SMALL_HEIGHT: 96,
  CAROUSAL_SMALL_WIDTH: 152,
  CAROUSAL_SMALL_MARGIN: 8,
  CAROUSAL_LABEL_BORDER_RADIUS: 16,
  CAROUSAL_LABEL_HEIGHT: 32,
  CAROUSAL_LABEL_MARGIN: 8,
  CAROUSAL_LABEL_PADDING_HORIZONTAL: 16,
  CAROUSAL_AGE_CONTAINER_SIZE: 280,
  CAROUSAL_AGE_SIZE_WITH_MARGIN: 56,
  CAROUSAL_AGE_SIZE: 40,
  CAROUSAL_AGE_MARGIN_HORIZONTAL: 8,
  CAROUSAL_ROOM_HEIGHT: 244,
  CAROUSAL_ROOM_TITLE_MARGIN_TOP: 153,
  CAROUSAL_ROOM_SEPARATOR_MARGIN_TOP: 4,
  CAROUSAL_ROOM_DOTS_CONTAINER_BOTTOM: 8,
  CAROUSAL_ROOM_DOTS_CONTAINER_PADDING_HORIZONTAL: 16,
  CAROUSAL_FULLSCREEN_CROSS_PADDING: 8,
  CAROUSAL_DETAILS_HEIGHT: 142,
  CAROUSAL_ROOM_TYPE_HEIGHT: 375,
  CAROUSAL_ROOM_TYPE_IMAGE_HEIGHT: 168,
  CAROUSAL_FULL_IMAGE_HEIGHT: 250,
  CAROUSAL_DOT_CONTAINER_WIDTH: 20,
}

export function isEmpty(str: string) {
  return !str || str.length === 0
}
export const ImageUrlDetection = (value: string) => {
  if (typeof value === 'string') {
    if (isEmpty(value)) {
      return 'defaultPlaceHolder'
    }
  }
  return value
}

const CardScheme = {
  VISA: 104,
  MASTER_CARD: 105,
  RUPAY: 199,
  DINERS: 107,
  AMEX: 103,
  CUP: 133,
  DISCOVER: 108,
  EuroPay: 106,
  JCB: 109,
}

export const getMaskedCardNumber = (number: String) => {
  const maskedNumber = number.slice(0, 4) + number.slice(4, number.length - 4).replace(/\d/g, '•') + number.slice(-4)
  return maskedNumber
}

export const getCardVendor = (scheme: number) => {
  switch (scheme) {
    case CardScheme.VISA:
      return 'visa'
    case CardScheme.MASTER_CARD:
      return 'mastercard'
    case CardScheme.RUPAY:
      return 'rupay'
    case CardScheme.DINERS:
      return 'diners'
    case CardScheme.AMEX:
      return 'amex'
    case CardScheme.DISCOVER:
      return 'discover'
    case CardScheme.JCB:
      return 'jcb'
    case CardScheme.EuroPay:
      return 'europay'
    case CardScheme.CUP:
      return 'cup'
    default:
      return 'none'
  }
}

export const getCardLogo = (cardId: number) => {
  switch (cardId) {
    case CardScheme.DINERS:
      return '../assets/images/ic_cards_diner.png'
    case CardScheme.DISCOVER:
      return '../assets/images/ic_cards_discover.png'
    default:
      return undefined
  }
}

export const useIsLargeScreen = () => {
  const [dimensions, setDimensions] = React.useState(Dimensions.get('window'))

  React.useEffect(() => {
    const onDimensionsChange = ({ window }: { window: ScaledSize }) => {
      setDimensions(window)
    }

    const dimenListenr: any = Dimensions.addEventListener('change', onDimensionsChange)
    return () => dimenListenr.remove()
  }, [])
  if (Platform.OS !== 'web') {
    return false
  }
  return dimensions.width > 550
}

export const CARD_TYPE = {
  DEBIT_CARD: '110',
  CREDIT_CARD: '130',
  FOOD_CARD: '140',
}

export const getCardType = (type: string) => {
  if (type === CARD_TYPE.CREDIT_CARD) {
    return 'Credit Card'
  } else if (type === CARD_TYPE.DEBIT_CARD) {
    return 'Debit Card'
  } else {
    return 'Food Card'
  }
}

export const mobileAndTabletCheck = () => {
  let check = false
  ;(function (a) {
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
        a,
      ) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        a?.substr(0, 4),
      )
    ) {
      check = true
    }
  })(navigator?.userAgent || navigator?.vendor || window?.opera)
  return check
}

export const useWindowDimensions = () => {
  const [width, setWidth] = useState(Dimensions.get('window').width)
  const isDesktop = Dimensions.get('window').width >= 1024
  const isMobileDevice = mobileAndTabletCheck()
  useEffect(() => {
    let subscription: any
    if (!isMobileDevice) {
      subscription = Dimensions.addEventListener('change', ({ window }) => {
        setWidth(window.width)
      })
    }
    return () => {
      subscription?.remove()
    }
  }, [isDesktop, isMobileDevice, width])
  return width
}

//used by useEffectDebugger Hook
const compareInputs = (oldInputs: any, newInputs: any, prefix: any) => {
  // Edge-case: different array lengths
  if (oldInputs.length !== newInputs.length) {
    // Not helpful to compare item by item, so just output the whole array
    console.log(`${prefix} - Inputs have a different length ====>`, oldInputs, newInputs)
    console.log('Old inputs: ====>', oldInputs)
    console.log('New inputs: ====>', newInputs)
    return
  }

  // Compare individual items
  oldInputs.forEach((oldInput: any, index: number) => {
    const newInput = newInputs[index]
    if (oldInput !== newInput) {
      console.log(`${prefix} - The input changed in position ====> ${index}`)
      console.log('Old value: ====>', oldInput)
      console.log('New value: ====>', newInput)
    }
  })
}

//Way to determine which variable in a useEffect's dependency array triggers a function re-fire
export const useEffectDebugger = (func: any, inputs: any, prefix = 'useEffect') => {
  // Using a ref to hold the inputs from the previous run (or same run for initial run
  const oldInputsRef = React.useRef(inputs)
  useEffect(() => {
    // Get the old inputs
    const oldInputs = oldInputsRef.current

    // Compare the old inputs to the current inputs
    compareInputs(oldInputs, inputs, prefix)

    // Save the current inputs
    oldInputsRef.current = inputs

    // Execute wrapped effect
    func()
  }, [func, inputs, prefix])

  /** USE IT AS ->
   * useEffectDebugger(() => {
   which variable triggered this re-fire?
   console.log('---useEffect---')
}, [a, b, c, d], 'Effect Name')
   */
}
